import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArchiveRestore,
  CheckCircle2,
  FileArchive,
  Loader2,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { CatalogueService, type Catalogue } from "@/services/catalogue.service";
import {
  ModerationRulesetService,
  type ModerationImportPreview,
  type ModerationReadiness,
  type ModerationRule,
  type ModerationRuleset,
  type ModerationRulesetAssignment,
  type ModerationRuleVersion,
} from "@/services/moderation-ruleset.service";

export function RulesetDetailsDialog({
  ruleset,
  onClose,
  onSaved,
}: {
  ruleset: ModerationRuleset | null;
  onClose: () => void;
  onSaved: (ruleset: ModerationRuleset) => void | Promise<void>;
}) {
  const [name, setName] = useState(ruleset?.name ?? "");
  const [description, setDescription] = useState(ruleset?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const saved = ruleset
        ? await ModerationRulesetService.updateRuleset(ruleset.id, {
            name: name.trim(),
            description: description.trim() || null,
          })
        : await ModerationRulesetService.createRuleset({
            name: name.trim(),
            description: description.trim() || null,
          });
      await onSaved(saved);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The ruleset could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{ruleset ? "Edit ruleset" : "Create ruleset"}</DialogTitle>
          <DialogDescription>
            Group related moderation rules and assign the ruleset to one or more catalogues.
          </DialogDescription>
        </DialogHeader>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="ruleset-name">Name</Label>
            <Input
              id="ruleset-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Marketplace product policy"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ruleset-description">Description</Label>
            <Textarea
              id="ruleset-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe where and why this ruleset should be used."
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => void save()} disabled={!name.trim() || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? "Saving…" : ruleset ? "Save changes" : "Create ruleset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ImportRulesetDialog({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (ruleset: ModerationRuleset) => void | Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ModerationImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function selectFile(selected: File | null) {
    setFile(selected);
    setPreview(null);
    setError(null);
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith(".zip")) {
      setError("Choose a moderation ruleset ZIP file.");
      return;
    }
    setPreviewing(true);
    try {
      setPreview(await ModerationRulesetService.previewImport(selected));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The archive could not be inspected.");
    } finally {
      setPreviewing(false);
    }
  }

  async function importRuleset() {
    if (!file || !preview) return;
    setImporting(true);
    setError(null);
    try {
      const imported = await ModerationRulesetService.importRuleset(file);
      await onImported(imported);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The ruleset could not be imported.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !importing && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Import ruleset</DialogTitle>
          <DialogDescription>
            Import creates a new, unassigned ruleset. Its rules start at v1 and prepare automatically.
          </DialogDescription>
        </DialogHeader>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        <label className="flex cursor-pointer flex-col items-center rounded-md border border-dashed p-8 text-center hover:bg-muted/30 focus-within:ring-2 focus-within:ring-ring">
          <FileArchive className="mb-3 h-8 w-8 text-muted-foreground" />
          <span className="text-sm font-medium">{file?.name ?? "Choose a ruleset ZIP"}</span>
          <span className="mt-1 text-xs text-muted-foreground">Maximum archive size: 55 MB</span>
          <input
            type="file"
            accept=".zip,application/zip"
            className="sr-only"
            onChange={(event) => void selectFile(event.target.files?.[0] ?? null)}
          />
        </label>
        {previewing ? (
          <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking archive…
          </div>
        ) : null}
        {preview ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
            <div className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              Ready to import
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              <div><dt className="opacity-70">Ruleset</dt><dd>{preview.rulesetName}</dd></div>
              <div><dt className="opacity-70">Rules</dt><dd>{preview.ruleCount}</dd></div>
              <div><dt className="opacity-70">References</dt><dd>{preview.referenceImageCount}</dd></div>
            </dl>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>Cancel</Button>
          <Button onClick={() => void importRuleset()} disabled={!preview || importing}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {importing ? "Importing…" : "Import as new ruleset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CatalogueAssignmentRow {
  catalogue: Catalogue;
  assignment: ModerationRulesetAssignment | null;
}

export function AssignmentsDialog({
  ruleset,
  rulesets,
  onClose,
  onChanged,
}: {
  ruleset: ModerationRuleset;
  rulesets: ModerationRuleset[];
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const [rows, setRows] = useState<CatalogueAssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [catalogues, assignmentLists] = await Promise.all([
        CatalogueService.getAll(),
        Promise.all(rulesets.map((item) => ModerationRulesetService.listAssignments(item.id))),
      ]);
      const assignments = new Map(
        assignmentLists.flat().map((assignment) => [assignment.catalogueId, assignment])
      );
      const nextRows = catalogues
        .filter((catalogue) => !catalogue.deletedAt)
        .map((catalogue) => ({
          catalogue,
          assignment: assignments.get(catalogue.id) ?? null,
        }));
      setRows(nextRows);
      setRemovingIds((current) => {
        const pending = new Set([...current].filter((catalogueId) => assignments.has(catalogueId)));
        return pending.size === current.size ? current : pending;
      });
      setError(null);
      return nextRows;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Catalogue assignments could not be loaded.");
      return null;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [rulesets]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasSyncingAssignment = rows.some((row) => row.assignment?.syncStatus === "syncing");

  useEffect(() => {
    if (!hasSyncingAssignment) return;
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      const nextRows = await load(false);
      if (!active) return;
      if (!nextRows || nextRows.some((row) => row.assignment?.syncStatus === "syncing")) {
        timer = window.setTimeout(() => void poll(), 2000);
        return;
      }
      await Promise.resolve(onChanged()).catch(() => undefined);
    };
    timer = window.setTimeout(() => void poll(), 2000);
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [hasSyncingAssignment, load, onChanged]);

  async function toggle(row: CatalogueAssignmentRow, assigned: boolean) {
    const currentRuleset = rulesets.find((item) => item.id === row.assignment?.rulesetId);
    if (
      assigned
      && row.assignment
      && row.assignment.rulesetId !== ruleset.id
      && !window.confirm(
        `Replace “${currentRuleset?.name ?? "the current ruleset"}” with “${ruleset.name}” for ${row.catalogue.name}?`
      )
    ) return;
    setBusyId(row.catalogue.id);
    setError(null);
    if (!assigned) {
      setRemovingIds((current) => new Set(current).add(row.catalogue.id));
    }
    try {
      if (assigned) {
        await ModerationRulesetService.setCatalogueAssignment(row.catalogue.id, ruleset.id);
      } else {
        await ModerationRulesetService.removeCatalogueAssignment(row.catalogue.id);
      }
      const nextRows = await load(false);
      const assignmentStillVisible = nextRows?.some(
        (item) => item.catalogue.id === row.catalogue.id && item.assignment
      );
      if (assigned || assignmentStillVisible === false) await onChanged();
    } catch (caught) {
      if (!assigned) {
        setRemovingIds((current) => {
          const next = new Set(current);
          next.delete(row.catalogue.id);
          return next;
        });
      }
      setError(caught instanceof Error ? caught.message : "The assignment could not be changed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !busyId && onClose()}>
      <DialogContent className="grid max-h-[calc(100vh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b px-6 py-5 pr-12">
          <DialogTitle>Catalogue assignments</DialogTitle>
          <DialogDescription>
            Choose which catalogues use “{ruleset.name}”. A catalogue can use one ruleset at a time.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto p-6">
          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
          {ruleset.archivedAt ? (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <ArchiveRestore className="h-4 w-4" /> Restore this ruleset before assigning it.
            </div>
          ) : null}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading catalogues…
            </div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No active catalogues are available.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {rows.map((row) => {
                const assignedHere = row.assignment?.rulesetId === ruleset.id;
                const otherRuleset = rulesets.find((item) => item.id === row.assignment?.rulesetId);
                const syncing = row.assignment?.syncStatus === "syncing";
                const needsAttention = row.assignment?.syncStatus === "needs_attention";
                const removing = removingIds.has(row.catalogue.id);
                const status = removing && syncing
                  ? "Removing assignment…"
                  : removing && needsAttention
                    ? "Removal needs attention — try again"
                    : syncing
                      ? "Assignment syncing…"
                      : needsAttention
                        ? "Assignment needs attention"
                        : assignedHere
                          ? "Uses this ruleset"
                          : otherRuleset
                            ? `Currently uses ${otherRuleset.name}`
                            : "No ruleset assigned";
                return (
                  <div key={row.catalogue.id} className="flex items-center justify-between gap-4 p-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{row.catalogue.name}</p>
                      <p className={cn(
                        "truncate text-xs text-muted-foreground",
                        needsAttention && "text-red-600 dark:text-red-400"
                      )}>
                        {status}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {busyId === row.catalogue.id || syncing
                        ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        : null}
                      <Switch
                        checked={assignedHere}
                        disabled={Boolean(ruleset.archivedAt) || busyId !== null || syncing}
                        onCheckedChange={(checked) => void toggle(row, checked)}
                        aria-label={`${assignedHere ? "Unassign" : "Assign"} ${ruleset.name} ${assignedHere ? "from" : "to"} ${row.catalogue.name}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="border-t bg-background px-6 py-4">
          <Button onClick={onClose} disabled={Boolean(busyId)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VersionHistoryDialog({
  rulesetId,
  rule,
  onClose,
}: {
  rulesetId: string;
  rule: ModerationRule;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<ModerationRuleVersion[]>([]);
  const [selected, setSelected] = useState<ModerationRuleVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void ModerationRulesetService.listRuleVersions(rulesetId, rule.id)
      .then((items) => {
        if (active) setVersions([...items].sort((a, b) => b.version - a.version));
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "Version history could not be loaded.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [rule.id, rulesetId]);

  async function inspect(version: number) {
    setDetailLoading(true);
    setError(null);
    try {
      setSelected(await ModerationRulesetService.getRuleVersion(rulesetId, rule.id, version));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "This version could not be loaded.");
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="grid max-h-[calc(100vh-2rem)] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="border-b px-6 py-5 pr-12">
          <DialogTitle>{rule.name} history</DialogTitle>
          <DialogDescription>
            Each saved change is retained. Historical violations keep the name and slug from the version that ran.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto p-6">
          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading versions…
            </div>
          ) : versions.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No versions are available.</p>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => (
                <div key={version.version} className="rounded-md border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">Version {version.version}</p>
                        {version.version === rule.effectiveVersion ? <Badge variant="secondary">Effective</Badge> : null}
                        <ReadinessBadge readiness={version.readiness} />
                      </div>
                      <p className="mt-1 font-mono text-xs text-muted-foreground">{version.slug}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {new Date(version.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={detailLoading}
                      onClick={() => void inspect(version.version)}
                    >
                      View details
                    </Button>
                  </div>
                  {selected?.version === version.version ? (
                    <dl className="mt-4 grid gap-3 border-t pt-4 text-xs sm:grid-cols-2">
                      <div><dt className="text-muted-foreground">Name</dt><dd className="mt-0.5 text-sm">{selected.name}</dd></div>
                      <div><dt className="text-muted-foreground">Severity</dt><dd className="mt-0.5 capitalize text-sm">{selected.severity}</dd></div>
                      <div><dt className="text-muted-foreground">Runs</dt><dd className="mt-0.5 text-sm">{runAtLabel(selected.runAt)}</dd></div>
                      <div><dt className="text-muted-foreground">Images</dt><dd className="mt-0.5 text-sm">{selected.imageScope === "all_images" ? "All images (up to 6)" : "First image"}</dd></div>
                      <div className="sm:col-span-2"><dt className="text-muted-foreground">Description</dt><dd className="mt-0.5 whitespace-pre-wrap text-sm leading-6">{selected.description}</dd></div>
                      <div><dt className="text-muted-foreground">References</dt><dd className="mt-0.5 text-sm">{selected.referenceImages.length}</dd></div>
                      <div><dt className="text-muted-foreground">Violation action</dt><dd className="mt-0.5 text-sm">{selected.autoUnpublish ? "Auto-unpublish" : "Record for review"}</dd></div>
                    </dl>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function runAtLabel(value: ModerationRule["runAt"]): string {
  if (value === "before_enrichment") return "Before enrichment";
  if (value === "after_enrichment") return "After enrichment";
  return "Before and after";
}

function ReadinessBadge({ readiness }: { readiness: ModerationReadiness }) {
  const labels: Record<ModerationReadiness, string> = {
    preparing: "Preparing",
    ready: "Ready",
    needs_attention: "Needs attention",
  };
  return (
    <Badge
      variant="outline"
      className={cn(
        readiness === "ready" && "border-emerald-200 bg-emerald-50 text-emerald-700",
        readiness === "preparing" && "border-sky-200 bg-sky-50 text-sky-700",
        readiness === "needs_attention" && "border-red-200 bg-red-50 text-red-700"
      )}
    >
      {labels[readiness]}
    </Badge>
  );
}

function ErrorMessage({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
